/**
 * Column ids and labels shared by booking-dump reports. Must stay in sync with
 * `tideway-core` `BOOKING_REPORT_COLUMN_IDS` / the booking_dump XLSX template.
 */

export const BOOKING_COLUMN_IDS = [
  "source_id",
  "state",
  "tags",
  "carrier_contract",
  "route",
  "departure_date",
  "arrival_date",
  "effective_pcd",
  "cutoff_date",
  "stuffing_date",
  "ready_date",
  "vessel",
  "equipment",
  "load_type",
  "total_containers",
  "total_teu",
  "weight",
  "consignee",
  "shipper",
  "agent",
  "week",
  "service_trade",
  "commodity",
  "open_date",
  "sales_person",
  "snapshot_date",
  "pricing_total",
  "rebook_price",
  "best_saving",
  "bl_group",
  "bl_charge",
  "opened_by",
  "delay_days",
  "delay_original_etd",
  "delay_new_etd",
  "delay_source",
  "delay_days_feeder",
  "delay_original_etd_feeder",
  "delay_new_etd_feeder",
  "delay_days_mv",
  "delay_original_etd_mv",
  "delay_new_etd_mv",
  "etd_delay_leg",
  "eta_delay_days",
  "delay_original_eta",
  "delay_new_eta",
  "eta_delay_source",
  "dg_classes",
  "rolled_from",
  "exw_charge",
  "ts_stops",
] as const;

export type BookingColumnId = (typeof BOOKING_COLUMN_IDS)[number];

export const BOOKING_COLUMN_ID_SET = new Set<string>(BOOKING_COLUMN_IDS);

/**
 * Bookings-table columns that booking-dump reports do not round-trip in
 * `config.columns`. When opening “View on bookings” from a scheduled report,
 * these keep the user’s saved table visibility and order.
 */
export const BOOKING_TABLE_ONLY_COLUMN_IDS = [
  "ready_date",
  "load_type",
  "total_containers",
  "weight",
  "best_saving",
] as const;

export type BookingTableOnlyColumnId =
  (typeof BOOKING_TABLE_ONLY_COLUMN_IDS)[number];

const BOOKING_TABLE_ONLY_COLUMN_ID_SET = new Set<string>(
  BOOKING_TABLE_ONLY_COLUMN_IDS,
);

/** Columns a saved report can drive on the bookings table (excludes table-only). */
export const BOOKING_REPORT_SYNC_COLUMN_IDS = BOOKING_COLUMN_IDS.filter(
  (id): id is BookingColumnId =>
    !BOOKING_TABLE_ONLY_COLUMN_ID_SET.has(id),
);

export const BOOKING_DEFAULT_VISIBLE_COLUMN_IDS: readonly BookingColumnId[] = [
  "source_id",
  "state",
  "tags",
  "carrier_contract",
  "route",
  "departure_date",
  "arrival_date",
  "equipment",
  "shipper",
  "agent",
];

export const BOOKING_COLUMN_LABELS: Record<BookingColumnId, string> = {
  source_id: "Booking #",
  state: "State",
  tags: "Tags",
  carrier_contract: "Carrier / Contract",
  route: "Route",
  departure_date: "ETD",
  arrival_date: "ETA",
  effective_pcd: "PCD",
  cutoff_date: "Cutoff",
  stuffing_date: "Stuffing",
  ready_date: "Ready",
  vessel: "Vessel",
  equipment: "Equipment",
  load_type: "Load",
  total_containers: "Ctrs",
  total_teu: "TEU",
  weight: "Weight",
  consignee: "Consignee",
  shipper: "Shipper",
  agent: "Agent",
  week: "Week",
  service_trade: "Service trade",
  commodity: "Commodity",
  open_date: "Opened",
  sales_person: "Sales",
  snapshot_date: "Last update",
  pricing_total: "Original price",
  rebook_price: "Rebook price",
  best_saving: "Saving",
  bl_group: "BL group",
  bl_charge: "BL charge",
  opened_by: "Opened by",
  delay_days: "ETD delay (days)",
  delay_original_etd: "Original ETD",
  delay_new_etd: "New ETD",
  delay_source: "ETD delay source",
  delay_days_feeder: "Feeder ETD delay (days)",
  delay_original_etd_feeder: "Feeder original ETD",
  delay_new_etd_feeder: "Feeder ETD (tracking)",
  delay_days_mv: "M/V ETD delay (days)",
  delay_original_etd_mv: "M/V original ETD",
  delay_new_etd_mv: "M/V ETD (tracking)",
  etd_delay_leg: "ETD delay leg",
  eta_delay_days: "ETA delay (days)",
  delay_original_eta: "Original ETA",
  delay_new_eta: "New ETA",
  eta_delay_source: "ETA delay source",
  dg_classes: "DG classes",
  rolled_from: "Rolled from",
  exw_charge: "EXW charge",
  ts_stops: "T/S stops",
};

export function bookingColumnLabel(id: string): string {
  return BOOKING_COLUMN_LABELS[id as BookingColumnId] ?? id;
}

export function resolveBookingReportColumns(
  config?: Record<string, unknown> | null,
): string[] {
  const fromConfig = config?.columns;
  if (Array.isArray(fromConfig) && fromConfig.length > 0) {
    return fromConfig.filter(
      (c): c is string => typeof c === "string" && BOOKING_COLUMN_ID_SET.has(c),
    );
  }
  return [...BOOKING_DEFAULT_VISIBLE_COLUMN_IDS];
}

export interface BookingReportColumnDisplay {
  id: string;
  visible: boolean;
}

export function columnsToDisplayPreference(
  columns: string[],
): BookingReportColumnDisplay[] {
  const selected = new Set(columns);
  const visibleItems = columns.map((id) => ({ id, visible: true }));
  const hiddenItems = BOOKING_COLUMN_IDS.filter((id) => !selected.has(id)).map(
    (id) => ({ id, visible: false }),
  );
  return [...visibleItems, ...hiddenItems];
}

export function displayPreferenceToColumns(
  display: BookingReportColumnDisplay[],
): string[] {
  return display.filter((item) => item.visible).map((item) => item.id);
}

export const BOOKING_REPORT_GROUP_BY_OPTIONS = [
  { value: "", label: "No grouping" },
  { value: "route", label: "Route" },
  { value: "service_trade", label: "Service trade" },
  { value: "week", label: "Week" },
  { value: "carrier", label: "Carrier" },
  { value: "state", label: "State" },
  { value: "bl_group", label: "BL group" },
  { value: "customer", label: "Consignee" },
  { value: "agent", label: "Agent" },
  { value: "sales_person", label: "Sales" },
  { value: "opened_by", label: "Opened by" },
] as const;

export const BOOKING_REPORT_SORT_FIELD_OPTIONS = [
  { value: "open_date", label: "Opened" },
  { value: "ready_date", label: "Ready date" },
  { value: "stuffing_date", label: "Stuffing" },
  { value: "cutoff_date", label: "Cutoff" },
  { value: "departure_date", label: "ETD" },
  { value: "arrival_date", label: "ETA" },
  { value: "pcd", label: "PCD" },
  { value: "snapshot_date", label: "Last update" },
  { value: "carrier", label: "Carrier" },
  { value: "state", label: "State" },
  { value: "agent", label: "Agent" },
  { value: "origin_port", label: "Origin" },
  { value: "destination_port", label: "Destination" },
  { value: "pricing_total", label: "Price" },
  { value: "source_id", label: "Booking #" },
] as const;

const REPORT_SORT_VALUES: ReadonlySet<string> = new Set(
  BOOKING_REPORT_SORT_FIELD_OPTIONS.map((option) => option.value),
);

/** Map a bookings-table `sort_by` key onto a report sort field. */
export function reportSortFromTable(sortBy?: string): string | undefined {
  if (!sortBy) return undefined;
  const mapped =
    sortBy === "effective_cutoff"
      ? "cutoff_date"
      : sortBy === "effective_pcd"
        ? "pcd"
        : sortBy;
  return REPORT_SORT_VALUES.has(mapped) ? mapped : undefined;
}

/** Map a booking-report `sort_by` field onto a bookings-table sort key. */
export function tableSortFromReport(sortBy?: string): string | undefined {
  if (!sortBy) return undefined;
  const mapped =
    sortBy === "cutoff_date"
      ? "effective_cutoff"
      : sortBy === "pcd"
        ? "pcd"
        : sortBy;
  return REPORT_SORT_VALUES.has(sortBy) ? mapped : undefined;
}

export const BOOKING_REPORT_DEDUP_FIELD_OPTIONS = BOOKING_COLUMN_IDS.map(
  (id) => ({
    value: id,
    label: BOOKING_COLUMN_LABELS[id],
  }),
);

const EXCLUDE_SUFFIX = "(exclude)";

function withExclude(key: string, label: string): Record<string, string> {
  return {
    [key]: label,
    [`${key}_not`]: `${label} ${EXCLUDE_SUFFIX}`,
  };
}

export const BOOKING_REPORT_FILTER_LABELS: Record<string, string> = {
  ...withExclude("state", "State"),
  ...withExclude("carrier", "Carrier"),
  ...withExclude("agent", "Agent"),
  ...withExclude("origin_port", "Origin"),
  ...withExclude("destination_port", "Destination"),
  ...withExclude("contract_ref", "Contract"),
  ...withExclude("week", "Week"),
  ...withExclude("service_trade", "Service trade"),
  departure_from: "ETD from",
  departure_to: "ETD to",
  etd_window: "ETD window",
  open_date_from: "Opened from",
  open_date_to: "Opened to",
  open_date_window: "Opened",
  ready_date_from: "Ready date from",
  ready_date_to: "Ready date to",
  last_updated_window: "Last updated",
  ...withExclude("load_type", "Load type"),
  ...withExclude("shipper", "Shipper"),
  ...withExclude("consignee", "Consignee"),
  ...withExclude("sales_person", "Sales"),
  ...withExclude("opened_by", "Opened by"),
  ...withExclude("tag", "Tag"),
  pcd_from: "PCD from",
  pcd_to: "PCD to",
  cutoff_from: "Cutoff from",
  cutoff_to: "Cutoff to",
  stuffing_from: "Stuffing from",
  stuffing_to: "Stuffing to",
  eta_from: "ETA from",
  eta_to: "ETA to",
  eta_window: "ETA window",
  last_updated_from: "Last updated from",
  last_updated_to: "Last updated to",
  last_updated_within_days: "Last updated within (days)",
  has_opportunity: "Has opportunity",
  favorites_only: "Starred only",
  search: "Search",
  rebook_from_contract: "Rebook from contract",
  rebook_to_contract: "Rebook to contract",
  equipment_size: "Equipment size",
  equipment_type: "Equipment type",
  weight_gte: "Weight ≥ (kg)",
  weight_lte: "Weight ≤ (kg)",
  weight_eq: "Weight = (kg)",
  total_containers_gte: "Ctrs ≥",
  total_containers_lte: "Ctrs ≤",
  total_containers_eq: "Ctrs =",
  delay_days_gte: "ETD delay ≥ (days)",
  delay_days_lte: "ETD delay ≤ (days)",
  delay_days_eq: "ETD delay = (days)",
  ...withExclude("delay_source", "ETD delay source"),
  eta_delay_days_gte: "ETA delay ≥ (days)",
  eta_delay_days_lte: "ETA delay ≤ (days)",
  eta_delay_days_eq: "ETA delay = (days)",
  ...withExclude("eta_delay_source", "ETA delay source"),
};

/** Visible table column ids that are valid booking-dump export columns. */
export function tableColumnsToReportColumns(columnIds: string[]): string[] {
  const mapped = columnIds.filter((id) => BOOKING_COLUMN_ID_SET.has(id));
  return mapped.length > 0 ? mapped : [...BOOKING_DEFAULT_VISIBLE_COLUMN_IDS];
}
